import { useEffect, useState, useCallback } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useColourTools } from "../hooks/useColourTools";
import { normalizeHex } from "../../../utils/colourUtils";
import type { ColourTarget } from "../../../types";
import { ReplaceColorsModal } from "./ReplaceColorsModal";
import { NonBrandModal } from "./NonBrandModal";
import { QuickActionsSection } from "./QuickActionsSection";
import { PaletteSection } from "./PaletteSection";
import { CleanUpStyling } from "./CleanUpStyling";
import { ErrorCircleFilled } from "@fluentui/react-icons";
import styles from "./ColourToolsPanel.module.css";

interface ColourToolsPanelProps {
  readonly onBack?: () => void;
}

export function ColourToolsPanel({ onBack }: Readonly<ColourToolsPanelProps>) {
  const tools = useColourTools();

  // Palette / colour picker state
  const [target, setTarget] = useState<ColourTarget>("fill");
  // Always stored WITHOUT a leading "#" — matches the raw hex colourOps and
  // the palette (`PaletteCell.hex`) already use, so this can be compared
  // against and displayed alongside them with one consistent representation
  // instead of two ("#0B5DAA" from the custom input vs "0B5DAA" from a
  // swatch) that used to collide into "##0B5DAA" on display.
  const [selectedHex, setSelectedHex] = useState<string | null>(null);
  const [customHex, setCustomHex] = useState("#0B5DAA");

  // Modal state
  const [showReplace, setShowReplace] = useState(false);
  const [showNonBrandModal, setShowNonBrandModal] = useState(false);

  // Clean up panel
  const [showCleanup, setShowCleanup] = useState(false);

  // Load theme on mount. `tools.loadTheme` is memoized with a stable
  // (empty-deps) identity in useColourTools, so listing it here is both
  // exhaustive-deps-correct and still genuinely run-once in practice.
  useEffect(() => {
    void tools.loadTheme();
  }, [tools.loadTheme]);

  const applySelectedColor = useCallback(
    (hex: string) => {
      const applyByTarget: Record<ColourTarget, (hex: string) => void> = {
        fill: tools.applyFill,
        font: tools.applyFont,
        line: tools.applyLine,
      };
      applyByTarget[target](hex);
    },
    [target, tools],
  );

  const handleSwatchClick = useCallback(
    (hex: string) => {
      setSelectedHex(hex);
      setCustomHex("#" + hex);
      applySelectedColor(hex);
    },
    [applySelectedColor],
  );

  // Custom colour applies immediately on pick (no separate Apply step), to
  // match the POC. Normalize to the same "no leading #" representation the
  // palette/selectedHex use so everything compares consistently.
  const handleCustomPick = useCallback(
    (hexWithHash: string) => {
      setCustomHex(hexWithHash);
      const hex = normalizeHex(hexWithHash);
      if (!hex) return;
      applySelectedColor(hex);
      setSelectedHex(hex);
    },
    [applySelectedColor],
  );

  // Replace Colors requires a shape/text box already selected before it
  // opens at all, regardless of which "Apply to" scope gets picked once
  // it's open — requireSelectionForReplace sets tools.error (auto-
  // dismissing, shown in the banner below) and resolves false when nothing
  // is selected, so the modal simply doesn't open instead of coming up
  // empty/confusing.
  const handleOpenReplace = useCallback(async () => {
    const ok = await tools.requireSelectionForReplace();
    if (ok) setShowReplace(true);
  }, [tools]);

  const handleOpenNonBrand = useCallback(() => {
    setShowNonBrandModal(true);
  }, []);

  return (
    <div className={styles.wrapper}>
      <ModuleHeader title="Colour Tools" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.panel}>
        <p className={styles.selectionNote}>Works on the shapes selected in PowerPoint.</p>

        {/* ── Heading card ── */}
        <div className={styles.card}>
          <div className={styles.headingRow}>
            <span className={styles.sectionHeading}>Colour Tools</span>
            {tools.themeColors && (
              <span className={styles.themeIndicator}>theme loaded</span>
            )}
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={() => tools.loadTheme()}
              disabled={tools.themeLoading || tools.busy}
              title="Reload theme colours"
              aria-label="Reload theme colours"
              data-testid="refresh-theme"
            >
              ↻
            </button>
          </div>
          {tools.themeError && (
            <div className={styles.errorInline}>{tools.themeError}</div>
          )}

          <QuickActionsSection
            busy={tools.busy}
            hasTheme={tools.themeColors != null}
            onClearFill={() => tools.clearFill()}
            onClearLine={() => tools.clearLine()}
            onShuffle={() => tools.shuffle(target)}
            onOpenReplace={handleOpenReplace}
            onOnBrandFill={() => tools.onBrandFill()}
            onOpenNonBrand={handleOpenNonBrand}
          />

          {/* Status row */}
          <div className={styles.statusRow}>
            {tools.error ? (
              <div className={styles.errorBanner} role="alert">
                <ErrorCircleFilled className={styles.errorBannerIcon} />
                <span className={styles.errorBannerText}>{tools.error}</span>
                <button
                  type="button"
                  className={styles.errorBannerCloseBtn}
                  onClick={() => tools.clearError()}
                  aria-label="Close"
                  data-testid="clear-error"
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                {tools.loadingLabel && (
                  <>
                    <span className={styles.spinner} />
                    <span className={styles.statusText}>{tools.loadingLabel}</span>
                  </>
                )}
                {tools.status && !tools.busy && (
                  <span className={styles.statusText} data-testid="status-text">{tools.status}</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Palette card ── */}
        <div className={styles.card}>
          <PaletteSection
            target={target}
            onTargetChange={setTarget}
            palette={tools.palette}
            themeLoading={tools.themeLoading}
            busy={tools.busy}
            selectedHex={selectedHex}
            customHex={customHex}
            onCustomPick={handleCustomPick}
            onSwatchClick={handleSwatchClick}
          />
        </div>

        {/* ── Clean Up card (collapsible) ── */}
        <CleanUpStyling
          open={showCleanup}
          onToggle={() => setShowCleanup((v) => !v)}
          busy={tools.busy}
          onCleanUp={() => tools.cleanUp()}
          cleanUpResult={tools.cleanUpResult}
        />

        {/* ── Replace Colors Modal ── */}
        <ReplaceColorsModal
          open={showReplace}
          onClose={() => setShowReplace(false)}
          tools={tools}
        />

        {/* ── Non-Brand Modal ── */}
        <NonBrandModal
          open={showNonBrandModal}
          onClose={() => setShowNonBrandModal(false)}
          tools={tools}
        />
      </div>
    </div>
  );
}
